import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import {
  Spinner,
  Card,
  Button,
  Row,
  Col,
  Badge,
  Alert,
} from "react-bootstrap";
import { FiFileText, FiCheckCircle } from "react-icons/fi";
import { motion } from "framer-motion";

/* ================= STATUS PPDB ================= */
const getStatusPpdb = (jadwal = []) => {
  if (!Array.isArray(jadwal)) return "TUTUP";

  const daftar = jadwal.find(j =>
    j.tahap?.toLowerCase().includes("pendaftaran")
  );

  if (!daftar?.mulai || !daftar?.selesai) return "TUTUP";

  const now = new Date();

  // PERBAIKAN: tambahkan waktu supaya full day
  const mulai = new Date(daftar.mulai + "T00:00:00");
  const selesai = new Date(daftar.selesai + "T23:59:59");

  if (now < mulai) return "BELUM";
  if (now > selesai) return "TUTUP";
  return "BUKA";
};

const SiswaDashboard = () => {
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [ppdbStatus, setPpdbStatus] = useState("TUTUP");
  const [statusSiswa, setStatusSiswa] = useState(null); // status pribadi siswa
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          navigate("/login");
          return;
        }

        // Ambil data user
        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (!userSnap.exists()) {
          setLoading(false);
          return;
        }
        setUserData(userSnap.data());

        // Ambil dokumen PPDB untuk jadwal
        const ppdbSnap = await getDocs(collection(db, "ppdb"));
        if (!ppdbSnap.empty) {
          // Cari dokumen khusus info PPDB (admin biasanya buat dokumen jadwal)
          // kita ambil dokumen pertama yang ada jadwalnya
          const ppdbInfoDoc = ppdbSnap.docs.find(d => Array.isArray(d.data().jadwal));
          if (ppdbInfoDoc) {
            const status = getStatusPpdb(ppdbInfoDoc.data().jadwal);
            setPpdbStatus(status);
          }
        }

        // Ambil dokumen siswa yang login
        const q = query(collection(db, "ppdb"), where("email", "==", user.email));
        const siswaSnap = await getDocs(q);
        if (!siswaSnap.empty) {
          const siswaData = siswaSnap.docs[0].data();
          setStatusSiswa(siswaData.status || "Menunggu");
        } else {
          setStatusSiswa("Belum Daftar");
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [navigate]);

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Memuat dashboard...</p>
      </div>
    );
  }

  if (!userData) {
    return (
      <p className="text-center text-danger mt-5">
        Data pengguna tidak ditemukan
      </p>
    );
  }

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #e3f2fd, #ffffff)",
        paddingTop: "40px",
      }}
    >
      <div className="container">

        {/* ================= HEADER ================= */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Card className="shadow-lg border-0 rounded-4 p-4 mb-4 text-center">
            <h2 className="fw-bold text-primary mb-1">
              Selamat datang, {userData.nama} 🎓
            </h2>
            <p className="text-muted mb-3">{userData.email}</p>

            {/* Badge Pendaftaran (Global) */}
            <Badge
              bg={
                ppdbStatus === "BUKA"
                  ? "success"
                  : ppdbStatus === "BELUM"
                  ? "warning"
                  : "danger"
              }
              className="px-4 py-2 fs-6"
            >
              Pendaftaran PPDB: {ppdbStatus}
            </Badge>

            {/* Badge Status Pribadi */}
            {statusSiswa && (
              <Badge
                bg={
                  statusSiswa === "Diterima"
                    ? "success"
                    : statusSiswa === "Ditolak"
                    ? "danger"
                    : "warning"
                }
                className="mt-2 ms-2 px-3 py-2 fs-6"
              >
                Status Anda: {statusSiswa}
              </Badge>
            )}
          </Card>
        </motion.div>

        {/* ================= INFO ================= */}
        {ppdbStatus !== "BUKA" && (
          <Alert
            variant={ppdbStatus === "BELUM" ? "warning" : "danger"}
            className="text-center shadow-sm"
          >
            {ppdbStatus === "BELUM"
              ? "⏳ Pendaftaran PPDB belum dibuka. Silakan menunggu sesuai jadwal."
              : "⛔ Pendaftaran PPDB sudah ditutup."}
          </Alert>
        )}

        {/* ================= MENU ================= */}
        <Row className="g-4 justify-content-center mt-3">

          {/* DAFTAR PPDB */}
          <Col md={5} lg={4}>
            <motion.div whileHover={{ scale: 1.05 }}>
              <Card className="h-100 shadow border-0 rounded-4 text-center p-4">
                <FiFileText size={64} className="text-primary mb-3" />
                <h5 className="fw-bold">Daftar PPDB</h5>
                <p className="text-muted">
                  Pendaftaran siswa baru secara online
                </p>

                <Button
                  variant={
                    ppdbStatus === "BUKA"
                      ? "success"
                      : ppdbStatus === "BELUM"
                      ? "secondary"
                      : "danger"
                  }
                  disabled={ppdbStatus !== "BUKA"}
                  onClick={() => navigate("/pendaftaran")}
                  className="fw-semibold"
                >
                  {ppdbStatus === "BUKA" && "📝 Daftar Sekarang"}
                  {ppdbStatus === "BELUM" && "⏳ Belum Dibuka"}
                  {ppdbStatus === "TUTUP" && "⛔ Ditutup"}
                </Button>
              </Card>
            </motion.div>
          </Col>

          {/* STATUS */}
          <Col md={5} lg={4}>
            <motion.div whileHover={{ scale: 1.05 }}>
              <Card className="h-100 shadow border-0 rounded-4 text-center p-4">
                <FiCheckCircle size={64} className="text-success mb-3" />
                <h5 className="fw-bold">Status & Riwayat</h5>
                <p className="text-muted">
                  Status dan riwayat pendaftaran PPDB Anda
                </p>

                <Button
                  variant="info"
                  className="text-white fw-semibold"
                  onClick={() => navigate("/status-ppdb")}
                >
                  🔍 Lihat Status
                </Button>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default SiswaDashboard;
